import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from 'components/icons';

type Side = 'left' | 'right';

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  /** Accessible name. Set `hideTitle` to keep it for screen readers only. */
  title: string;
  hideTitle?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
  side?: Side;
  /** Tailwind width class for the panel, e.g. "w-80". */
  width?: string;
  /** Extra controls in the header, left of the close button. */
  headerActions?: React.ReactNode;
  /** Pinned bottom section, e.g. secondary nav or an upgrade card. */
  footer?: React.ReactNode;
  children: React.ReactNode;
}

const panelSide: Record<Side, { rest: string; from: string }> = {
  left: { rest: 'left-0 border-r', from: '-translate-x-full' },
  right: { rest: 'right-0 border-l', from: 'translate-x-full' },
};

/**
 * Slide-out panel counterpart to Modal. Same headlessui Dialog underneath, so
 * it gets the focus trap, Escape handling, focus restore, scroll lock, and
 * aria-modal that the two hand-rolled drawers in this app were missing — the
 * mobile sidebar and the chat history panel both let you tab into the page
 * behind them while open.
 *
 * Kept separate from Modal rather than bolted on as a prop: the two differ in
 * transition axis, sizing, and header shape, and merging them would mean a
 * component that does neither cleanly.
 */
const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  title,
  hideTitle = false,
  icon: Icon,
  side = 'right',
  width = 'w-80',
  headerActions,
  footer,
  children,
}) => {
  const { rest, from } = panelSide[side];

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/40" aria-hidden="true" />
        </Transition.Child>

        <Transition.Child
          as={Fragment}
          enter="transform transition ease-out duration-250"
          enterFrom={from}
          enterTo="translate-x-0"
          leave="transform transition ease-in duration-200"
          leaveFrom="translate-x-0"
          leaveTo={from}
        >
          <Dialog.Panel
            className={`fixed inset-y-0 ${rest} ${width} max-w-[85vw] bg-surface-raised border-border shadow-e3 flex flex-col`}
          >
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border bg-surface-sunk flex-shrink-0">
              <Dialog.Title
                className={
                  hideTitle
                    ? 'sr-only'
                    : 'text-title text-content flex items-center gap-2 min-w-0'
                }
              >
                {Icon && !hideTitle && (
                  <Icon className="h-5 w-5 text-content-muted shrink-0" aria-hidden="true" />
                )}
                <span className="truncate">{title}</span>
              </Dialog.Title>
              <div className="flex items-center gap-1 flex-shrink-0">
                {headerActions}
                <button
                  type="button"
                  onClick={onClose}
                  aria-label={`Close ${title}`}
                  className="p-1.5 rounded-token text-content-subtle hover:text-content hover:bg-surface focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">{children}</div>

            {footer && (
              <div className="border-t border-border flex-shrink-0">{footer}</div>
            )}
          </Dialog.Panel>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
};

export default Drawer;
